import type { ButtonProps } from '@ahamove/web-components/lib/Button';
import Button from '@ahamove/web-components/lib/Button';
import MuiContainer from '@ahamove/web-components/lib/Container';
import ListItem from '@ahamove/web-components/lib/ListItem';
import ListItemAvatar from '@ahamove/web-components/lib/ListItemAvatar';
import ListItemButton from '@ahamove/web-components/lib/ListItemButton';
import ListItemIcon from '@ahamove/web-components/lib/ListItemIcon';
import ListItemText from '@ahamove/web-components/lib/ListItemText';
import Menu from '@ahamove/web-components/lib/Menu';
import MenuItem from '@ahamove/web-components/lib/MenuItem';
import Skeleton from '@ahamove/web-components/lib/Skeleton';
import SvgIcon from '@ahamove/web-components/lib/SvgIcon';
import { Disclosure } from '@headlessui/react';
import cn from 'classnames';
import Image from 'next/legacy/image';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React from 'react';
import type { User } from '@/api/auth/types';
import { ArrowDownSimpleIcon, SignOutIcon, UserIcon } from '@/lib/icons';
import { useAuth } from '@/modules/auth/AuthProvider';
import useIsMobile from '@/shared-hooks/useIsMobile';
import { MenuIcon, XMenuIcon } from '../icons';
import ButtonNavbar from './ButtonNavbar';

type NavbarProps = {
  id: string;
  title: string;
  href?: string;
  icon?: 'Bike' | 'Booking';
  dataTestId?: string;
};

const headerData: NavbarProps[] = [
  {
    id: 'p1',
    title: 'rent_ev',
    href: '/',
    icon: 'Bike',
    dataTestId: 'goToHomePage',
  },
  {
    id: 'p2',
    title: 'my_bookings',
    href: '/bookings',
    icon: 'Booking',
    dataTestId: 'goToBookingPage',
  },
];

const LoginButton = (props: ButtonProps) => {
  const { t } = useTranslation(['common']);
  const { signIn } = useAuth();
  const isMobile = useIsMobile();

  return (
    <Button
      fullWidth={isMobile}
      rounded
      variant="outlined"
      color="inherit"
      title={t('common:header.login').toString()}
      className="desktop:block hidden flex-none"
      sx={{
        minWidth: 120,
        minHeight: 44,
        color: 'grey.600',
        bgcolor: 'common.white',
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: 'grey.100',
        px: 2,
        py: 1.5,
      }}
      onClick={() => signIn(`${window.location}`)}
      {...props}
      data-test-id="loginBtn"
    >
      {t('common:header.login')}
    </Button>
  );
};

const LoginLoadingButton = () => {
  const isMobile = useIsMobile();

  return (
    <ListItem
      sx={{
        maxHeight: 52,
        height: 52,
        maxWidth: isMobile ? '100%' : 222,
        width: '100%',
        px: 1.5,
        py: 1,
        bgcolor: 'common.white',
        borderRadius: 3,
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: 'grey.100',
      }}
    >
      <ListItemAvatar sx={{ minWidth: 28 }}>
        <Skeleton variant="rounded" height={24} width={24} />
      </ListItemAvatar>
      <ListItemText
        primary={<Skeleton variant="rounded" height={14} width={45} />}
        secondary={<Skeleton variant="rounded" height={20} width={120} />}
        secondaryTypographyProps={{ mt: `2px !important` }}
      />
    </ListItem>
  );
};

const UserProfileButton = ({ user }: { user: User }) => {
  const { t } = useTranslation(['common']);
  const { logOut } = useAuth();
  const isMobile = useIsMobile();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <ListItem
        sx={{
          maxHeight: 52,
          height: 52,
          maxWidth: isMobile ? '100%' : 222,
          width: '100%',
          p: 0,
          bgcolor: 'common.white',
          borderRadius: 3,
          borderWidth: 2,
          borderStyle: 'solid',
          borderColor: 'grey.100',
          overflow: 'hidden',
        }}
      >
        <ListItemButton
          disableRipple
          id="user-button"
          aria-controls={open ? 'user-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={isMobile ? undefined : handleClick}
          sx={{ pl: '16px !important', pr: '12px !important', py: 1, m: 0 }}
          dense
        >
          <ListItemAvatar sx={{ minWidth: 28 }}>
            <UserIcon />
          </ListItemAvatar>
          {user && (
            <ListItemText
              primary={t('common:header.account')}
              primaryTypographyProps={{
                fontSize: 10,
                lineHeight: 1.4,
                fontWeight: 600,
                color: 'grey.500',
              }}
              secondary={user.name}
              secondaryTypographyProps={{
                mt: `2px !important`,
                fontSize: `14px !important`,
                lineHeight: `20px !important`,
                fontWeight: `700 !important`,
                color: 'grey.900',
                noWrap: true,
                title: user.name,
              }}
            />
          )}
          {!isMobile ? (
            <SvgIcon
              viewBox="0 0 16 16"
              component={ArrowDownSimpleIcon}
              sx={{
                ml: 0.5,
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          ) : null}
        </ListItemButton>
      </ListItem>

      {isMobile ? (
        <Button
          fullWidth
          rounded
          variant="text"
          color="error"
          title={t('common:header.logout').toString()}
          className="desktop:block hidden flex-none"
          sx={{
            minWidth: 120,
            minHeight: 44,
            bgcolor: 'common.white',
            px: 2,
            py: 1.5,
            mt: 2,
          }}
          onClick={logOut}
          data-test-id="logoutBtn"
        >
          {t('common:header.logout')}
        </Button>
      ) : (
        <Menu
          id="user-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          PaperProps={{ elevation: 2, sx: { minWidth: 'auto' } }}
          MenuListProps={{
            'aria-labelledby': 'user-btn',
            sx: {
              py: 0,
            },
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem
            onClick={() => {
              logOut();
              handleClose();
            }}
          >
            <ListItemIcon>
              <SignOutIcon />
            </ListItemIcon>
            <ListItemText
              primary={t('common:header.logout')}
              primaryTypographyProps={{ variant: 'body2', color: 'error.main' }}
            />
          </MenuItem>
        </Menu>
      )}
    </>
  );
};

export default function Header() {
  const { locale, pathname } = useRouter();
  const router = useRouter();
  const { t } = useTranslation(['common']);
  const { user, isLoadingUser } = useAuth();
  const isMobile = useIsMobile();

  return (
    <Disclosure as="nav">
      {({ open }) => (
        <header
          className={cn('shadow-shadow4 fixed inset-x-0 top-0 z-50', {
            'bg-white/80 backdrop-blur-sm backdrop-saturate-150': !open,
            'bg-white': open,
          })}
        >
          <MuiContainer
            maxWidth="xl"
            sx={{
              px: isMobile ? 2 : 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              height: isMobile ? 80 : 84,
              position: 'relative',
            }}
          >
            {/* Mobile menu button */}
            <Disclosure.Button
              aria-label="menu"
              className="desktop:hidden absolute right-4 inline-flex items-center justify-center text-neutral-50"
            >
              {open ? (
                <XMenuIcon className="desktop:hover:fill-primary-50 fill-neutral-50" />
              ) : (
                <MenuIcon className="desktop:hover:fill-primary-50 fill-neutral-50" />
              )}
            </Disclosure.Button>
            <div className="desktop:flex-1 desktop:justify-between flex h-full flex-nowrap items-center justify-start">
              <NextLink
                href="/"
                passHref
                locale={locale}
                className="relative mr-4 inline-block h-9 w-[130px] flex-none bg-transparent"
                title="HomePage"
                aria-label="HomePage"
              >
                <Image
                  src="/static/icons/LogoEV.svg"
                  alt=""
                  layout="fill"
                  priority
                />
              </NextLink>
              <nav className="desktop:flex mr-auto ml-4 hidden h-full flex-row">
                {headerData.map((item) => (
                  <div key={item.id} className="inline-block h-full w-full">
                    <ButtonNavbar
                      title={item.title}
                      href={item.href}
                      icon={item.icon}
                      data-test-id={item.dataTestId}
                    />
                  </div>
                ))}
              </nav>

              {!isMobile ? (
                <>
                  {user ? (
                    <UserProfileButton user={user} />
                  ) : isLoadingUser ? (
                    <LoginLoadingButton />
                  ) : (
                    <LoginButton />
                  )}
                </>
              ) : null}
            </div>
          </MuiContainer>
          <Disclosure.Panel className="desktop:hidden bg-white">
            <div className="flex h-[calc(100vh-80px)] flex-col">
              <div className="overflow-x-none grow overflow-y-auto pt-2 pb-6 shadow-inner">
                {headerData.map((item) => (
                  <div key={item.id}>
                    <button
                      aria-label="button"
                      className={cn(
                        'text-title24 inline-flex w-full justify-start px-4 py-3 font-semibold',
                        {
                          'text-primary-60 cursor-no-drop transition-none':
                            pathname === item.href,
                          'text-neutral-90 desktop:hover:text-primary-50':
                            pathname !== item.href,
                        }
                      )}
                      data-test-id="logoBtn"
                      onClick={() => router.push(item.href ? item.href : '/')}
                    >
                      {t(`common:page.${item.title}`)}
                    </button>
                  </div>
                ))}
                <div className="p-4">
                  {user ? (
                    <UserProfileButton user={user} />
                  ) : isLoadingUser ? (
                    <LoginLoadingButton />
                  ) : (
                    <LoginButton />
                  )}
                </div>
              </div>
            </div>
          </Disclosure.Panel>
        </header>
      )}
    </Disclosure>
  );
}
